<template>
  <UInput
    v-model="datesValue"
    data-test="cottage-prices-aside-date-input"
    :placeholder="t('components.search.dates')"
    :aria-label="t('components.search.dates')"
    :title="t('components.search.dates-title')"
    :size="size"
    readonly
    :disabled="itemId && !disableDates ? true : false"
    class="col-span-2 lg:col-span-3"
    :icon="showIconMobile ? 'i-heroicons-calendar-days-20-solid' : undefined"
    :ui="variant === 'bigInput' ? bigInput : {}"
    autocomplete="on"
    @click="toggleModal(true)"
    @keydown.space="toggleModal(true)"
    @keydown.enter.prevent="toggleModal(true)"
  />
  <UModal
    v-model:open="isOpen"
    :ui="calendarModal"
    :overlay="false"
    :fullscreen="!isLargeScreen"
    @after-leave="toggleModal(false)"
  >
    <template #body>
      <TDateRange
        v-model="dates"
        data-test="t-date-input-date-range"
        :disable-dates="disableDates"
        @close="toggleModal(false)"
      >
      </TDateRange>
    </template>

    <template #footer>
      <ClientOnly>
        <TText v-if="message" class="text-xs" classes="text-center">
          {{ message }}
        </TText>
        <UButton
          v-if="props.clearDates"
          color="neutral"
          size="md"
          variant="subtle"
          @click="setWithoutDates()"
        >
          {{ t('components.search.withoutdates') }}
        </UButton>
      </ClientOnly>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import type { DatePickerRangeObject } from '@angelblanco/v-calendar/dist/types/src/use/datePicker.js'
import getClosedDates from '~~/modules/api/rurality/v3/cottages/detail/closed-dates'
import { useToTDateInput } from '~/composables/transformers/useToTDateInput'
import { bigInput } from '~~/modules/design-system/utils/nuxt-ui-config/components/input'
import { calendarModal } from '~~/modules/design-system/utils/nuxt-ui-config/components/modal'

interface Props {
  dates: DatePickerRangeObject | null
  itemId?: string
  message?: string
  clearDates?: boolean
  showIconMobile?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'default' | 'bigInput'
  openModal?: boolean
}

export interface disableDatesInterface {
  start: string
  end: string
}

const { fromClosedDates } = useToTDateInput()
const { isLargeScreen } = useResponsive()

const props = withDefaults(defineProps<Props>(), {
  clearDates: false,
  showIconMobile: true,
  size: 'xl',
  variant: 'default',
  openModal: false,
})

const emit = defineEmits(['update:dates', 'update:openModal'])

const isOpen = ref(false)
const dates = ref(props.dates)
const disableDates = ref<disableDatesInterface[]>([])

const toggleModal = (state: boolean) => {
  isOpen.value = state
  emit('update:openModal', isOpen.value)
}

const setWithoutDates = () => {
  dates.value = null
  emit('update:dates', null)
  isOpen.value = false
}

const datesValue = computed(() => {
  if (dates.value?.start && dates.value?.end) {
    const startString = toRuralityDate().displayDate(dates.value.start as Date)
    const endString = toRuralityDate().displayDate(dates.value.end as Date)
    return `${startString} - ${endString}`
  }
  return ''
})

watch(
  () => props.dates,
  newDates => {
    dates.value = newDates
  },
  { immediate: true },
)

watch(
  dates,
  newDates => {
    emit('update:dates', newDates)
  },
  { deep: true },
)

watch(
  () => props.openModal,
  newOpenModal => {
    isOpen.value = newOpenModal
  },
)

onMounted(async () => {
  if (import.meta.client && props.itemId) {
    const disableDatesFetch = await getClosedDates({ id: props.itemId })
    if (disableDatesFetch.success) {
      disableDates.value = fromClosedDates(disableDatesFetch.success.data)
    }
  }
})
</script>
